import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    Typography,
    IconButton,
    Chip,
    Alert
} from '@mui/material';
import { Close as CloseIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

const MetadatosDeteccionMalezaModal = ({ 
    open, 
    onClose, 
    onConfirm, 
    isProcessing 
}) => {
    const [formData, setFormData] = useState({
        zafra: '',
        tercio: '',
        fechaFoto: null,
        precioPorHa: ''
    });
    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Limpiar error cuando el usuario comience a escribir
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.zafra.trim()) {
            newErrors.zafra = 'La zafra es requerida';
        } else if (!/^\d{2}-\d{2}$/.test(formData.zafra.trim())) {
            newErrors.zafra = 'La zafra debe tener el formato XX-XX (ej: 25-26)';
        }
        
        if (!formData.tercio) {
            newErrors.tercio = 'El tercio es requerido';
        }
        
        if (!formData.fechaFoto) {
            newErrors.fechaFoto = 'La fecha de la foto es requerida';
        }
        // Precio por hectárea
        if (formData.precioPorHa === '' || formData.precioPorHa === null) {
            newErrors.precioPorHa = 'El precio por hectárea es requerido';
        } else if (isNaN(Number(formData.precioPorHa))) {
            newErrors.precioPorHa = 'Ingrese un número válido';
        } else if (Number(formData.precioPorHa) < 0) {
            newErrors.precioPorHa = 'El precio no puede ser negativo';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onConfirm({
                zafra: formData.zafra.trim(),
                tercio: formData.tercio,
                fechaFoto: formData.fechaFoto.toISOString(),
                precioPorHa: Number(formData.precioPorHa)
            });
        }
    };

    const handleClose = () => {
        setFormData({
            zafra: '',
            tercio: '',
            fechaFoto: null
        });
        setErrors({});
        onClose();
    };

    const currentYear = dayjs().year();
    const nextYear = currentYear + 1;
    const currentZafra = `${currentYear.toString().slice(-2)}-${nextYear.toString().slice(-2)}`;

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: 3
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6" component="span">
                        Datos del Análisis de Maleza
                    </Typography>
                </Box>
                <IconButton 
                    onClick={handleClose} 
                    size="small"
                    disabled={isProcessing}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Estos datos se asociarán al análisis de detección de maleza y se mostrarán en el dashboard.
                </Alert>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    
                    <TextField
                        label="Zafra"
                        value={formData.zafra}
                        onChange={(e) => handleChange('zafra', e.target.value)}
                        error={!!errors.zafra}
                        helperText={errors.zafra || 'Formato: XX-XX'}
                        fullWidth
                        variant="outlined"
                        placeholder={currentZafra}
                        disabled={isProcessing}
                    />
                    
                    <TextField
                        label="Tercio"
                        value={formData.tercio}
                        onChange={(e) => handleChange('tercio', e.target.value)}
                        error={!!errors.tercio}
                        helperText={errors.tercio}
                        fullWidth
                        variant="outlined"
                        select
                        disabled={isProcessing}
                    >
                        <MenuItem value="1">1er Tercio</MenuItem>
                        <MenuItem value="2">2do Tercio</MenuItem>
                        <MenuItem value="3">3er Tercio</MenuItem>
                    </TextField>
                    
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <DatePicker
                            label="Fecha de la Foto"
                            value={formData.fechaFoto}
                            onChange={(date) => handleChange('fechaFoto', date)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    error={!!errors.fechaFoto}
                                    helperText={errors.fechaFoto}
                                    disabled={isProcessing}
                                />
                            )}
                            format="DD/MM/YYYY"
                            maxDate={dayjs()}
                            disabled={isProcessing}
                        />
                    </LocalizationProvider>

                    <TextField
                        label="Precio (USD) por hectárea"
                        value={formData.precioPorHa}
                        onChange={(e) => handleChange('precioPorHa', e.target.value)}
                        error={!!errors.precioPorHa}
                        helperText={errors.precioPorHa || 'Ej: 150.50'}
                        fullWidth
                        variant="outlined"
                        type="number"
                        inputProps={{ step: '0.01', min: '0' }}
                        disabled={isProcessing}
                    />
                </Box>
                
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Vista previa de datos:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip 
                            label={`Zafra: ${formData.zafra || 'Sin especificar'}`}
                            size="small"
                            variant="outlined"
                        />
                        <Chip 
                            label={`Tercio: ${formData.tercio || 'Sin especificar'}`}
                            size="small"
                            variant="outlined"
                        />
                        <Chip 
                            label={`Fecha: ${formData.fechaFoto ? formData.fechaFoto.format('DD/MM/YYYY') : 'Sin especificar'}`}
                            size="small"
                            variant="outlined"
                        />
                        <Chip 
                            label={`Precio (USD/ha): ${formData.precioPorHa !== '' ? String(formData.precioPorHa) : 'Sin especificar'}`}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
                <Button 
                    onClick={handleClose} 
                    variant="outlined"
                    disabled={isProcessing}
                >
                    Cancelar
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained"
                    disabled={isProcessing}
                    sx={{ minWidth: 120 }}
                >
                    {isProcessing ? 'Procesando...' : 'Confirmar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MetadatosDeteccionMalezaModal;
